import { useState } from 'react'
import {
  addExercise,
  deleteExercise,
  deleteSetLog,
  logSet,
  updateExercise,
} from '../../budget/repo'
import { useStore } from '../../store'
import { Card, Chip, Sheet, TopBar } from '../../ui/parts'
import {
  allProgress,
  formatKg,
  logsFor,
  parseWeight,
  youtubeId,
  type BodyTag,
  type Exercise,
  type ExerciseProgress,
} from '../exercise'

type Filter = BodyTag | 'all'

export function ExerciseScreen() {
  const { snapshot } = useStore()
  const [filter, setFilter] = useState<Filter>('all')
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<Exercise | null>(null)

  if (!snapshot) return null

  const progress = allProgress(snapshot.exercise, filter)

  return (
    <div className="screen">
      <TopBar title="Exercise" subtitle="Progressive overload" />

      <div className="stack">
        <div className="filters">
          {(['all', 'upper', 'lower'] as Filter[]).map((option) => (
            <button
              key={option}
              type="button"
              className={`filter ${filter === option ? 'on' : ''}`}
              onClick={() => setFilter(option)}
            >
              {option === 'all' ? 'All' : option === 'upper' ? 'Upper body' : 'Lower body'}
            </button>
          ))}
        </div>

        {progress.length === 0 ? (
          <Card>
            <div className="muted" style={{ fontSize: 14, textAlign: 'center', padding: '14px 0' }}>
              No exercises yet. Add one to start tracking.
            </div>
          </Card>
        ) : null}

        {progress.map((item) => (
          <ExerciseCard key={item.exercise.id} progress={item} onEdit={() => setEditing(item.exercise)} />
        ))}
      </div>

      <button type="button" className="fab" onClick={() => setAdding(true)} aria-label="Add exercise">
        +
      </button>

      {adding || editing ? (
        <ExerciseSheet
          editing={editing}
          onClose={() => {
            setAdding(false)
            setEditing(null)
          }}
        />
      ) : null}
    </div>
  )
}

function ExerciseCard({ progress, onEdit }: { progress: ExerciseProgress; onEdit: () => void }) {
  const { snapshot, mutate, showToast } = useStore()
  const [weight, setWeight] = useState('')
  const [completed, setCompleted] = useState(true)
  const [showHistory, setShowHistory] = useState(false)

  if (!snapshot) return null

  const { exercise, lastLog, targetKg, bestKg, changePct } = progress
  const parsed = parseWeight(weight)
  const video = youtubeId(exercise.youtubeUrl)
  const history = logsFor(snapshot.exercise, exercise.id).slice(-8).reverse()

  return (
    <Card>
      <div className="row between">
        <div className="grow">
          <div className="row" style={{ gap: 8 }}>
            <span className="card-title">{exercise.title}</span>
            <Chip tone={exercise.tag === 'upper' ? 'indigo' : 'lilac'}>
              {exercise.tag === 'upper' ? 'Upper' : 'Lower'}
            </Chip>
          </div>
          {exercise.description ? (
            <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
              {exercise.description}
            </div>
          ) : null}
        </div>
        <button type="button" className="icon-btn" onClick={onEdit} aria-label="Edit exercise">
          ⋯
        </button>
      </div>

      <div className="stats" style={{ marginTop: 12 }}>
        <div className="stat">
          <div className="grow">
            <div className="lbl">Next target</div>
            <div className="stat-value" style={{ color: 'var(--primary)' }}>
              {targetKg === null ? 'Log a set' : formatKg(targetKg)}
            </div>
          </div>
        </div>
        <div className="stat">
          <div className="grow">
            <div className="lbl">Best</div>
            <div className="stat-value">{formatKg(bestKg)}</div>
          </div>
        </div>
      </div>

      <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
        {lastLog ? (
          <>
            Last: {formatKg(lastLog.weightKg)} on {lastLog.date} —{' '}
            {lastLog.completed ? 'full set' : 'not completed'}
            {changePct !== null ? ` · ${changePct > 0 ? '+' : ''}${Math.round(changePct)}% in 30 days` : ''}
          </>
        ) : (
          'Nothing logged yet'
        )}
      </div>

      <div className="row" style={{ marginTop: 10 }}>
        <input
          type="text"
          inputMode="decimal"
          placeholder={targetKg !== null ? String(targetKg) : 'Weight (kg)'}
          value={weight}
          className={weight !== '' && parsed === null ? 'error' : ''}
          onChange={(e) => setWeight(e.target.value)}
          style={{ flex: 1 }}
        />
        <button
          type="button"
          className="btn small"
          disabled={parsed === null && targetKg === null}
          onClick={async () => {
            const value = parsed ?? targetKg
            if (value === null) return
            const saved = await mutate((s) =>
              logSet(s, { exerciseId: exercise.id, weightKg: value, completed })
            )
            if (saved) {
              setWeight('')
              showToast(
                completed
                  ? `Logged ${formatKg(value)} — next target ${formatKg(value + 2.5)}`
                  : `Logged ${formatKg(value)} — target stays ${formatKg(value)}`
              )
            }
          }}
        >
          Log set
        </button>
      </div>

      <button
        type="button"
        className="row"
        style={{
          width: '100%',
          marginTop: 8,
          background: 'none',
          border: 0,
          padding: 0,
          font: 'inherit',
          color: 'inherit',
          cursor: 'pointer',
          textAlign: 'left',
        }}
        onClick={() => setCompleted(!completed)}
      >
        <span className={`switch ${completed ? 'on' : ''}`} aria-hidden />
        <span style={{ marginLeft: 10, fontSize: 14 }}>
          Completed the full set
          <span className="muted" style={{ display: 'block', fontSize: 12 }}>
            {completed ? 'Target goes up next time' : 'Target stays where it is'}
          </span>
        </span>
      </button>

      {video ? (
        <a
          href={`https://www.youtube.com/watch?v=${video}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'inline-block', marginTop: 10, fontSize: 13, color: 'var(--primary)' }}
        >
          Watch the form video
        </a>
      ) : null}

      {history.length > 0 ? (
        <>
          <button
            type="button"
            className="btn ghost small"
            style={{ paddingLeft: 0, marginTop: 6 }}
            onClick={() => setShowHistory(!showHistory)}
          >
            {showHistory ? 'Hide history' : `History (${progress.logCount})`}
          </button>
          {showHistory
            ? history.map((log) => (
                <div className="row between" key={log.id} style={{ padding: '5px 0' }}>
                  <span className="muted" style={{ fontSize: 13 }}>
                    {log.date}
                  </span>
                  <span className="tnum" style={{ fontSize: 14 }}>
                    {formatKg(log.weightKg)} {log.completed ? '✓' : '·'}
                  </span>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label="Delete set"
                    onClick={() => void mutate((s) => deleteSetLog(s, log.id))}
                  >
                    ✕
                  </button>
                </div>
              ))
            : null}
        </>
      ) : null}
    </Card>
  )
}

function ExerciseSheet({ editing, onClose }: { editing: Exercise | null; onClose: () => void }) {
  const { mutate, showToast } = useStore()
  const [title, setTitle] = useState(editing?.title ?? '')
  const [description, setDescription] = useState(editing?.description ?? '')
  const [youtubeUrl, setYoutubeUrl] = useState(editing?.youtubeUrl ?? '')
  const [tag, setTag] = useState<BodyTag>(editing?.tag ?? 'upper')

  const urlLooksWrong = youtubeUrl.trim() !== '' && youtubeId(youtubeUrl) === null

  return (
    <Sheet onClose={onClose}>
      <h2 style={{ margin: '0 0 14px', fontSize: 20 }}>
        {editing ? 'Edit exercise' : 'Add exercise'}
      </h2>

      <label className="field">
        <span>Title</span>
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
      </label>

      <label className="field">
        <span>Description</span>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>

      <label className="field">
        <span>YouTube video (optional)</span>
        <input
          type="url"
          inputMode="url"
          placeholder="https://youtu.be/…"
          value={youtubeUrl}
          className={urlLooksWrong ? 'error' : ''}
          onChange={(e) => setYoutubeUrl(e.target.value)}
        />
      </label>
      {urlLooksWrong ? (
        <div className="over" style={{ fontSize: 12, marginTop: -6, marginBottom: 8 }}>
          That does not look like a YouTube link — it will be saved but not linked.
        </div>
      ) : null}

      <div className="segmented" style={{ marginBottom: 14 }}>
        <button type="button" className={tag === 'upper' ? 'on' : ''} onClick={() => setTag('upper')}>
          Upper body
        </button>
        <button type="button" className={tag === 'lower' ? 'on' : ''} onClick={() => setTag('lower')}>
          Lower body
        </button>
      </div>

      <div className="row">
        <button
          type="button"
          className="btn grow"
          disabled={title.trim() === ''}
          onClick={async () => {
            const input = { title, description, youtubeUrl, tag }
            const saved = await mutate((s) =>
              editing ? updateExercise(s, editing.id, input) : addExercise(s, input)
            )
            if (saved) {
              onClose()
              showToast(editing ? 'Exercise updated' : `Added "${title.trim()}"`)
            }
          }}
        >
          {editing ? 'Save changes' : 'Add exercise'}
        </button>
        {editing ? (
          <button
            type="button"
            className="btn danger"
            onClick={async () => {
              const id = editing.id
              onClose()
              await mutate((s) => deleteExercise(s, id))
              showToast('Exercise and its history removed')
            }}
          >
            Delete
          </button>
        ) : null}
        <button type="button" className="btn ghost" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Sheet>
  )
}
